import { randomBytes } from "node:crypto";
import { cancel, isCancel, select } from "@clack/prompts";
import type { SpaceMeta } from "@wakeloop/protocol";
import type {
  AccountSpaceIndexItem,
  AgentProfileBindingExecutionTarget,
  PrepareSetupBootstrapResult,
  PrepareSetupBootstrapStep,
} from "@wakeloop/schemas/types";
import { listAccountSpaceIndex } from "../config/account-space-index-api";
import {
  getAuthSessionState,
  readActiveOrganizationIdFromSessionPayload,
  type StoredActiveOrganization,
  type StoredAuthSession,
  updateAuthSessionActiveOrganization,
} from "../config/auth-session";
import {
  resolveGatewayHost,
  upsertProfileSpaceHistory,
} from "../config/profile-space-history-api";
import {
  ensureWakeLoopManagedAgentProfile,
  resolveWakeLoopProfile,
  type WakeLoopProfile,
} from "../config/wakeloop-profiles-api";
import { buildWakeLoopRequestContextFromProfile } from "../config/wakeloop-request-context";
import type { SelectedAgentControllerReportResult } from "../local-service/reporting/selected-agent-controller-reporting";
import { listAuthOrganizations } from "../org/auth-organization-api";
import { resolveSetupFollowUpCliEntryCommand } from "../runtime/cli-entry-command";
import { createCliSpaceApi } from "../space/cli-space-api";
import { buildSpaceWebUrl } from "../space/web-url";
import {
  DEFAULT_REMOTE_SESSION_TIMEOUT_MS,
  LOOPBACK_REMOTE_SESSION_TIMEOUT_MS,
} from "../strategy-constants";
import { resolveAgentProfilePrimaryAgentControllerReport } from "../system/agent-profile-primary-binding";
import {
  type AgentProfileRuntimeBindingEnsureResult,
  type AgentProfileRuntimeBindingExistingBinding,
  ensureAgentProfileRuntimeBinding,
} from "../system/ensure-agent-profile-runtime-binding";
import type { SetupProgressStepHandle } from "../ui/setup-progress";
import { buildSetupAssistantBootstrapWelcomeMessage } from "./setup-assistant-bootstrap-context";

const WAKELOOP_ASSISTANT_MANAGED_ROLE = "wakeloop_assistant" as const;
const WELCOMING_SPACE_PURPOSE = "wakeloop_welcoming_space" as const;
const WELCOMING_SPACE_HISTORY_STATUS = "active" as const;
const WELCOME_MESSAGE_PURPOSE = "setup_bootstrap_welcome" as const;
const TRAILING_SLASHES_RE = /\/+$/u;

export type SetupBootstrapStep = PrepareSetupBootstrapStep;
export type SetupBootstrapResult = PrepareSetupBootstrapResult;
type SetupBootstrapWarning = Extract<
  SetupBootstrapResult,
  { status: "completed" }
>["warnings"][number];
type SetupBootstrapBinding = NonNullable<
  Extract<SetupBootstrapResult, { status: "completed" }>["binding"]
>;
type BootstrapWelcomingSpace = {
  createdAt: string;
  creatorKind: "human" | "agent" | "system" | null;
  creatorProfileId: string | null;
  name: string;
  outcome: "created" | "reused";
  spaceId: string;
  updatedAt: string | null;
  url: string;
};
type SetupBootstrapLineEmitter = (input: {
  code: string;
  data?: Record<string, unknown>;
  text: string;
}) => void;
type AssistantBindingCandidate = {
  agentId: string;
  report: SelectedAgentControllerReportResult;
};
type AssistantBindingTarget = {
  agentId: string;
  report: SelectedAgentControllerReportResult;
  writeMode?: "reconnect";
};
type ExistingAssistantBindingResolution =
  | {
      status: "matched_selected_report";
      target: AssistantBindingTarget;
    }
  | {
      agentLabel: string;
      status: "selected_without_report";
    }
  | {
      agentLabel: string;
      status: "not_selected";
    };

function resolveSetupBootstrapRecoveryCommand(
  failedStep: SetupBootstrapStep | "unknown"
): string {
  const cliEntry = resolveSetupFollowUpCliEntryCommand();
  return failedStep === "binding"
    ? `${cliEntry} doctor --view agent`
    : `${cliEntry} setup`;
}

export async function runSetupFirstBootstrap(input: {
  gatewayUrl: string;
  interactive?: boolean;
  line?: SetupBootstrapLineEmitter;
  progress?: SetupProgressStepHandle;
  runtimeReports?: readonly SelectedAgentControllerReportResult[];
  selectedLocalAgentIds: readonly string[];
}): Promise<SetupBootstrapResult> {
  try {
    return await runSetupFirstBootstrapOrThrow(input);
  } catch (error) {
    const stepError = error instanceof SetupBootstrapStepError ? error : null;
    const cause = stepError?.causeValue ?? error;
    const failedStep = stepError?.step ?? "unknown";
    const message = toErrorMessage(cause);
    console.warn("[wakeloop.setup.bootstrap.failed]", {
      event: "cli.setup.bootstrap.failed",
      error: message,
      failedStep,
      selectedAgentIds: [...input.selectedLocalAgentIds],
    });
    return {
      status: "failed",
      diagnostics: {
        errorName: readErrorName(cause),
        selectedAgentIds: [...input.selectedLocalAgentIds],
      },
      failedStep,
      message,
      reason: "bootstrap_failed",
      retryCommand: resolveSetupBootstrapRecoveryCommand(failedStep),
    };
  }
}

async function runSetupFirstBootstrapOrThrow(input: {
  gatewayUrl: string;
  interactive?: boolean;
  line?: SetupBootstrapLineEmitter;
  progress?: SetupProgressStepHandle;
  runtimeReports?: readonly SelectedAgentControllerReportResult[];
  selectedLocalAgentIds: readonly string[];
}): Promise<Extract<SetupBootstrapResult, { status: "completed" }>> {
  const humanProfile = await runBootstrapStep(
    "human_profile",
    input.progress,
    "Checking your WakeLoop profile",
    async () =>
      await resolveWakeLoopProfile({
        profileKind: "human",
      })
  );
  const userName = resolveBootstrapUserName(humanProfile);
  const assistantProfile = await runBootstrapStep(
    "assistant_profile",
    input.progress,
    "Preparing your WakeLoop Assistant profile",
    async () =>
      await ensureWakeLoopManagedAgentProfile({
        creatorProfileId: humanProfile.wakeLoopProfileId,
        managedRole: WAKELOOP_ASSISTANT_MANAGED_ROLE,
        profileName: buildSetupAssistantProfileName(userName),
      })
  );

  assertManagedAssistantProfile(assistantProfile);

  const activeOrganizationSlug = await runBootstrapStep(
    "account_space_index",
    input.progress,
    "Reading the active organization for Space links",
    async () => await resolveActiveOrganizationSlug()
  );
  const binding = await runBootstrapStep(
    "binding",
    input.progress,
    input.selectedLocalAgentIds.length > 0
      ? "Connecting WakeLoop Assistant to the selected local agent"
      : "Checking whether WakeLoop Assistant needs a local-agent Binding",
    async () =>
      await bindAssistantToSelectedLocalAgent({
        assistantProfile,
        interactive: input.interactive === true,
        line: input.line,
        runtimeReports: input.runtimeReports ?? [],
        selectedLocalAgentIds: input.selectedLocalAgentIds,
      })
  );
  const humanRequestContext = buildWakeLoopRequestContextFromProfile({
    wakeLoopProfile: humanProfile,
  });
  const baseSpaceName = buildSetupWelcomingSpaceName(userName);
  const existingWelcomingSpace = await runBootstrapStep(
    "account_space_index",
    input.progress,
    "Checking for an existing WakeLoop Welcoming Space",
    async () =>
      await resolveExistingWelcomingSpaceForAccount({
        activeOrganizationSlug,
        gatewayUrl: input.gatewayUrl,
        humanProfile,
      })
  );
  const spaceApi = createCliSpaceApi(input.gatewayUrl);
  const welcomingSpace =
    existingWelcomingSpace ??
    (await runBootstrapStep(
      "space_create",
      input.progress,
      "Creating your WakeLoop Welcoming Space",
      async () =>
        await createSetupWelcomingSpace({
          activeOrganizationSlug,
          baseSpaceName,
          gatewayUrl: input.gatewayUrl,
          humanProfile,
          humanRequestContext,
          spaceApi,
        })
    ));

  const membership = await runBootstrapStep(
    "space_membership",
    input.progress,
    "Adding WakeLoop Assistant to the Welcoming Space",
    async () =>
      await spaceApi.addMembers({
        memberProfileIds: [assistantProfile.wakeLoopProfileId],
        requestContext: humanRequestContext,
        spaceId: welcomingSpace.spaceId,
      })
  );

  const welcomeText = buildSetupWelcomeMessage(assistantProfile.profileName);
  const welcomeMessage =
    welcomingSpace.outcome === "created"
      ? await runBootstrapStep(
          "welcome_message",
          input.progress,
          "Posting the WakeLoop Assistant welcome message",
          async () => {
            const assistantRequestContext =
              buildWakeLoopRequestContextFromProfile({
                wakeLoopProfile: assistantProfile,
              });
            return await spaceApi.postNormalMessage({
              meta: {
                purpose: WELCOME_MESSAGE_PURPOSE,
              },
              requestContext: assistantRequestContext,
              spaceId: welcomingSpace.spaceId,
              text: welcomeText,
            });
          }
        )
      : null;

  const warnings = await syncBootstrapProfileSpaceHistory({
    assistantProfile,
    gatewayUrl: input.gatewayUrl,
    humanProfile,
    welcomingSpace,
  });

  return {
    status: "completed",
    assistantProfile: {
      wakeLoopProfileId: assistantProfile.wakeLoopProfileId,
      managedRole: WAKELOOP_ASSISTANT_MANAGED_ROLE,
      profileKind: "agent",
      profileName: assistantProfile.profileName,
    },
    binding,
    humanProfile: {
      wakeLoopProfileId: humanProfile.wakeLoopProfileId,
      profileKind: "human",
      profileName: humanProfile.profileName,
    },
    facts: {
      ownerUserId: humanProfile.ownerUserId,
      assistantProfileId: assistantProfile.wakeLoopProfileId,
      spaceId: welcomingSpace.spaceId,
      spaceUrl: welcomingSpace.url,
      createdAt: welcomingSpace.createdAt,
    },
    membership: {
      addedMemberIds: [...membership.addedMemberIds],
      alreadyMemberIds: [...membership.alreadyMemberIds],
      assistantProfileId: assistantProfile.wakeLoopProfileId,
      humanProfileId: humanProfile.wakeLoopProfileId,
    },
    retryCommand: null,
    warnings,
    welcomeMessage: welcomeMessage
      ? {
          signalId: welcomeMessage.id,
          text: welcomeText,
        }
      : null,
    welcomingSpace: {
      createdAt: welcomingSpace.createdAt,
      name: welcomingSpace.name,
      outcome: welcomingSpace.outcome,
      spaceId: welcomingSpace.spaceId,
      url: welcomingSpace.url,
    },
  };
}

export function buildSetupAssistantProfileName(userName: string): string {
  return `${userName}'s WakeLoop Assistant`;
}

export function buildSetupWelcomingSpaceName(userName: string): string {
  return `${userName}'s WakeLoop Welcoming Space`;
}

async function resolveExistingWelcomingSpaceForAccount(input: {
  activeOrganizationSlug: string;
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
}): Promise<BootstrapWelcomingSpace | null> {
  const requestContext = buildWakeLoopRequestContextFromProfile({
    wakeLoopProfile: input.humanProfile,
  });
  const index = await listAccountSpaceIndex({
    baseUrl: input.gatewayUrl,
    includePurposes: true,
    limit: 500,
    requestContext,
    scope: "account",
    selectedProfileId: input.humanProfile.wakeLoopProfileId,
  });
  const item =
    [...index.pinnedItems, ...index.spaceItems].find((candidate) =>
      isReusableSetupWelcomingSpace({
        humanProfile: input.humanProfile,
        item: candidate,
      })
    ) ?? null;
  if (!item) {
    return null;
  }
  return toReusedWelcomingSpace({
    activeOrganizationSlug: input.activeOrganizationSlug,
    gatewayUrl: input.gatewayUrl,
    humanProfile: input.humanProfile,
    item,
  });
}

function isReusableSetupWelcomingSpace(input: {
  humanProfile: WakeLoopProfile;
  item: AccountSpaceIndexItem;
}): boolean {
  if (input.item.spacePurpose !== WELCOMING_SPACE_PURPOSE) {
    return false;
  }

  const creatorOwnerUserId = normalizeOptionalText(
    input.item.creatorOwnerUserId
  );
  if (creatorOwnerUserId) {
    return creatorOwnerUserId === input.humanProfile.ownerUserId;
  }

  return input.item.creatorProfileId === input.humanProfile.wakeLoopProfileId;
}

function assertManagedAssistantProfile(profile: WakeLoopProfile): void {
  if (profile.profileKind !== "agent") {
    throw new Error(
      `Auth returned a managed WakeLoop Assistant profile with profileKind ${profile.profileKind}.`
    );
  }
  if (profile.managedRole !== WAKELOOP_ASSISTANT_MANAGED_ROLE) {
    throw new Error(
      "Auth returned a managed WakeLoop Assistant profile without managedRole wakeloop_assistant."
    );
  }
}

async function resolveActiveOrganizationSlug(): Promise<string> {
  const authState = await getAuthSessionState({
    validateRemote: false,
  });
  if (authState.state !== "valid" || !authState.session) {
    throw new Error(
      "WakeLoop setup could not build the Welcoming Space link because the local sign-in session is not valid."
    );
  }

  const cachedSlug = normalizeOptionalText(
    authState.session.activeOrganization?.slug
  );
  if (cachedSlug) {
    return cachedSlug;
  }

  const activeOrganizationId = await fetchRemoteActiveOrganizationId(
    authState.session
  );
  if (!activeOrganizationId) {
    throw new Error(
      "WakeLoop setup could not build the Welcoming Space link because no active organization is selected."
    );
  }

  const activeOrganization = await resolveActiveOrganizationById({
    activeOrganizationId,
    session: authState.session,
  });
  if (!activeOrganization) {
    throw new Error(
      `WakeLoop setup could not build the Welcoming Space link because active organization ${activeOrganizationId} was not found.`
    );
  }

  await updateAuthSessionActiveOrganization(activeOrganization);
  return activeOrganization.slug;
}

async function fetchRemoteActiveOrganizationId(
  session: StoredAuthSession
): Promise<string | null> {
  const endpoint = toAuthEndpoint(session.authBaseUrl, "/api/auth/get-session");
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `${normalizeAuthTokenType(session.tokenType)} ${session.accessToken}`,
      },
      signal: AbortSignal.timeout(resolveRemoteSessionTimeoutMs(endpoint)),
    });
  } catch (error) {
    throw new Error(
      `WakeLoop setup could not verify the active organization because WakeLoop auth is unavailable: ${toErrorMessage(error)}`
    );
  }

  const payload = await parseResponseJson(response);
  if (response.status === 401 || response.status === 403) {
    throw new Error(
      "WakeLoop setup could not build the Welcoming Space link because the local sign-in session is no longer valid."
    );
  }
  if (!response.ok) {
    throw new Error(
      `WakeLoop setup could not verify the active organization because WakeLoop auth returned HTTP ${response.status}.`
    );
  }
  if (!hasActiveAuthSessionPayload(payload)) {
    throw new Error(
      "WakeLoop setup could not build the Welcoming Space link because the local sign-in session is not valid."
    );
  }

  return readActiveOrganizationIdFromSessionPayload(payload);
}

async function resolveActiveOrganizationById(input: {
  activeOrganizationId: string;
  session: StoredAuthSession;
}): Promise<StoredActiveOrganization | null> {
  const organizations = await listAuthOrganizations(input.session);
  return (
    organizations.find(
      (organization) => organization.id === input.activeOrganizationId
    ) ?? null
  );
}

function normalizeAuthTokenType(value: string | undefined): string {
  const normalized = normalizeOptionalText(value);
  return normalized ?? "Bearer";
}

function toAuthEndpoint(baseUrl: string, pathname: string): string {
  const url = new URL(baseUrl);
  const normalizedBasePath = url.pathname.replace(TRAILING_SLASHES_RE, "");
  const normalizedPathname = pathname.startsWith("/")
    ? pathname
    : `/${pathname}`;
  url.pathname = `${normalizedBasePath}${normalizedPathname}` || "/";
  url.search = "";
  url.hash = "";
  return url.toString();
}

function resolveRemoteSessionTimeoutMs(endpoint: string): number {
  const url = parseUrlOrNull(endpoint);
  if (url && isLoopbackHostname(url.hostname)) {
    return LOOPBACK_REMOTE_SESSION_TIMEOUT_MS;
  }
  return DEFAULT_REMOTE_SESSION_TIMEOUT_MS;
}

function parseUrlOrNull(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function isLoopbackHostname(hostname: string): boolean {
  return (
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1"
  );
}

async function parseResponseJson(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return { message: raw };
  }
}

function hasActiveAuthSessionPayload(payload: unknown): boolean {
  return (
    isRecord(payload) && isRecord(payload.session) && isRecord(payload.user)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

async function bindAssistantToSelectedLocalAgent(input: {
  assistantProfile: WakeLoopProfile;
  interactive: boolean;
  line?: SetupBootstrapLineEmitter;
  runtimeReports: readonly SelectedAgentControllerReportResult[];
  selectedLocalAgentIds: readonly string[];
}): Promise<SetupBootstrapBinding | null> {
  const selectedAgentIds = input.selectedLocalAgentIds
    .map(normalizeOptionalText)
    .filter((agentId): agentId is string => agentId !== null);
  const firstSelectedAgentId = selectedAgentIds[0] ?? null;
  if (!firstSelectedAgentId) {
    return null;
  }
  const selectedCandidates = resolveSelectedAssistantBindingCandidates({
    runtimeReports: input.runtimeReports,
    selectedAgentIds,
  });
  const selectedReports = selectedCandidates.map(
    (candidate) => candidate.report
  );

  const existingBinding = await resolveExistingAssistantBindingForSelection({
    assistantProfile: input.assistantProfile,
    candidates: selectedCandidates,
    selectedAgentIds,
  });
  if (existingBinding?.status === "matched_selected_report") {
    emitSetupBootstrapLine(input.line, {
      code: "setup.bootstrap.assistant_agent.existing",
      text: `WakeLoop Assistant runs on ${existingBinding.target.agentId} (existing connection).`,
    });
    return await bindAssistantToLocalAgent({
      assistantProfile: input.assistantProfile,
      selectedReports,
      target: existingBinding.target,
    });
  }
  if (existingBinding?.status === "selected_without_report") {
    throw new Error(
      `WakeLoop Assistant is already connected to local agent ${existingBinding.agentLabel}, but setup did not receive a current runtime report for ${existingBinding.agentLabel}. Select ${existingBinding.agentLabel} after it appears as available, or reconnect the Assistant to one of the reported local agents before continuing.`
    );
  }

  const selectedTarget = await resolveAssistantBindingTarget({
    candidates: selectedCandidates,
    existingBindingAgentLabel:
      existingBinding?.status === "not_selected"
        ? existingBinding.agentLabel
        : null,
    firstSelectedAgentId,
    interactive: input.interactive,
    line: input.line,
    ...(existingBinding?.status === "not_selected"
      ? { writeMode: "reconnect" as const }
      : {}),
  });
  return await bindAssistantToLocalAgent({
    assistantProfile: input.assistantProfile,
    selectedReports,
    target: selectedTarget,
  });
}

async function bindAssistantToLocalAgent(input: {
  assistantProfile: WakeLoopProfile;
  selectedReports: readonly SelectedAgentControllerReportResult[];
  target: AssistantBindingTarget;
}): Promise<SetupBootstrapBinding> {
  const binding = await ensureAgentProfileRuntimeBinding({
    profile: input.assistantProfile,
    runtimeReportSubmission: input.target.report.runtimeReportSubmission,
    ...(input.target.writeMode ? { writeMode: input.target.writeMode } : {}),
  });
  const fallbackTarget = resolvePrimaryConflictFallbackTarget({
    binding,
    originalTarget: input.target,
    selectedReports: input.selectedReports,
  });
  if (fallbackTarget) {
    const fallbackBinding = await ensureAgentProfileRuntimeBinding({
      profile: input.assistantProfile,
      runtimeReportSubmission: fallbackTarget.report.runtimeReportSubmission,
      ...(fallbackTarget.writeMode
        ? { writeMode: fallbackTarget.writeMode }
        : {}),
    });
    return requireAcceptedAssistantBinding({
      agentId: fallbackTarget.agentId,
      binding: fallbackBinding,
      report: fallbackTarget.report,
    });
  }
  return requireAcceptedAssistantBinding({
    agentId: input.target.agentId,
    binding,
    report: input.target.report,
  });
}

async function resolveExistingAssistantBindingForSelection(input: {
  assistantProfile: WakeLoopProfile;
  candidates: readonly AssistantBindingCandidate[];
  selectedAgentIds: readonly string[];
}): Promise<ExistingAssistantBindingResolution | null> {
  const existingBinding = await resolveAgentProfilePrimaryAgentControllerReport(
    {
      profile: input.assistantProfile,
    }
  );
  if (!existingBinding) {
    return null;
  }
  const selectedAgentIdSet = new Set(input.selectedAgentIds);
  const executionTarget = existingBinding.primaryBinding.executionTarget;
  const target = executionTarget
    ? resolveSelectedReportForExistingAssistantBinding({
        agentControllerRef: existingBinding.agentControllerRef,
        candidates: input.candidates,
        existingBindingExecutionTarget: executionTarget,
      })
    : null;
  if (target) {
    return {
      status: "matched_selected_report",
      target,
    };
  }

  const agentLabel =
    existingBinding.localAgentId ?? existingBinding.agentControllerRef;
  if (existingBinding.localAgentId && selectedAgentIdSet.has(agentLabel)) {
    return {
      agentLabel,
      status: "selected_without_report",
    };
  }
  return {
    agentLabel,
    status: "not_selected",
  };
}

function resolveSelectedReportForExistingAssistantBinding(input: {
  agentControllerRef: string;
  candidates: readonly AssistantBindingCandidate[];
  existingBindingExecutionTarget: AgentProfileBindingExecutionTarget;
}): AssistantBindingTarget | null {
  const wakeLoopServiceRuntimeId = normalizeOptionalText(
    input.existingBindingExecutionTarget.wakeLoopServiceRuntimeId.value
  );
  if (!wakeLoopServiceRuntimeId) {
    return null;
  }
  const candidate =
    input.candidates.find((candidate) => {
      return (
        candidate.report.agentControllerRef === input.agentControllerRef &&
        normalizeOptionalText(
          candidate.report.runtimeReportSubmission.wakeLoopServiceRuntimeId
        ) === wakeLoopServiceRuntimeId
      );
    }) ?? null;
  return candidate
    ? {
        agentId: candidate.agentId,
        report: candidate.report,
      }
    : null;
}

function resolveSelectedAssistantBindingCandidates(input: {
  runtimeReports: readonly SelectedAgentControllerReportResult[];
  selectedAgentIds: readonly string[];
}): AssistantBindingCandidate[] {
  return input.selectedAgentIds.flatMap((agentId) => {
    const report =
      input.runtimeReports.find(
        (candidate) =>
          candidate.agentId === agentId &&
          isVerifiedSelectedRuntimeReport(candidate)
      ) ?? null;
    return report ? [{ agentId, report }] : [];
  });
}

function isVerifiedSelectedRuntimeReport(
  report: SelectedAgentControllerReportResult
): boolean {
  return (
    report.localParticipationState === "ready" &&
    report.runtimeReportSubmission.status === "submitted" &&
    report.runtimeReportSubmission.agentControllerReportStatus === "accepted" &&
    normalizeOptionalText(
      report.runtimeReportSubmission.wakeLoopServiceRuntimeId
    ) !== null
  );
}

async function resolveAssistantBindingTarget(input: {
  candidates: readonly AssistantBindingCandidate[];
  existingBindingAgentLabel: string | null;
  firstSelectedAgentId: string;
  interactive: boolean;
  line?: SetupBootstrapLineEmitter;
  writeMode?: "reconnect";
}): Promise<AssistantBindingTarget> {
  if (input.candidates.length === 0) {
    throw new Error(
      `WakeLoop Assistant could not be connected to local agent ${input.firstSelectedAgentId} because setup did not receive its runtime report.`
    );
  }
  const candidate =
    input.interactive && input.candidates.length > 1
      ? await promptAssistantBindingCandidate({
          candidates: input.candidates,
          existingBindingAgentLabel: input.existingBindingAgentLabel,
        })
      : input.candidates[0];
  if (!candidate) {
    throw new Error(
      "WakeLoop Assistant could not be connected because no selected local agent was available."
    );
  }
  if (!input.interactive) {
    emitDefaultAssistantBindingDecision({
      agentId: candidate.agentId,
      existingBindingAgentLabel: input.existingBindingAgentLabel,
      line: input.line,
    });
  }
  return {
    agentId: candidate.agentId,
    report: candidate.report,
    ...(input.writeMode ? { writeMode: input.writeMode } : {}),
  };
}

async function promptAssistantBindingCandidate(input: {
  candidates: readonly AssistantBindingCandidate[];
  existingBindingAgentLabel: string | null;
}): Promise<AssistantBindingCandidate> {
  const selected = await select({
    initialValue: input.candidates[0]?.agentId,
    message: input.existingBindingAgentLabel
      ? `Assistant is currently connected to ${input.existingBindingAgentLabel}; pick its new agent.`
      : "Which local agent should run your WakeLoop Assistant?",
    options: input.candidates.map((candidate) => ({
      label: candidate.agentId,
      value: candidate.agentId,
    })),
  });
  if (isCancel(selected)) {
    cancel("Cancelled.");
    throw new Error(
      "Setup was cancelled before choosing which local agent should run WakeLoop Assistant."
    );
  }
  const selectedAgentId = normalizeOptionalText(selected);
  const candidate =
    input.candidates.find((item) => item.agentId === selectedAgentId) ?? null;
  if (!candidate) {
    throw new Error(
      "WakeLoop Assistant local agent selection did not match a verified local agent."
    );
  }
  return candidate;
}

function emitDefaultAssistantBindingDecision(input: {
  agentId: string;
  existingBindingAgentLabel: string | null;
  line?: SetupBootstrapLineEmitter;
}): void {
  const suffix = input.existingBindingAgentLabel
    ? ` (default for this session; currently connected to ${input.existingBindingAgentLabel}).`
    : " (default for this session).";
  emitSetupBootstrapLine(input.line, {
    code: "setup.bootstrap.assistant_agent.default",
    text: `WakeLoop Assistant runs on ${input.agentId}${suffix}`,
  });
}

function resolvePrimaryConflictFallbackTarget(input: {
  binding: AgentProfileRuntimeBindingEnsureResult;
  originalTarget: AssistantBindingTarget;
  selectedReports: readonly SelectedAgentControllerReportResult[];
}): AssistantBindingTarget | null {
  if (
    input.binding.status !== "rejected" ||
    !input.binding.reasonCodes.includes(
      "agent_profile_binding.primary_conflict"
    )
  ) {
    return null;
  }
  const existingBinding = input.binding.existingBinding ?? null;
  if (!existingBinding) {
    return null;
  }
  const matchingSelectedReport =
    input.selectedReports.find((candidate) =>
      runtimeReportMatchesExistingBinding(candidate, existingBinding)
    ) ?? null;
  if (matchingSelectedReport) {
    return {
      agentId: matchingSelectedReport.agentId,
      report: matchingSelectedReport,
    };
  }
  return {
    ...input.originalTarget,
    writeMode: "reconnect",
  };
}

function runtimeReportMatchesExistingBinding(
  report: SelectedAgentControllerReportResult,
  existingBinding: AgentProfileRuntimeBindingExistingBinding
): boolean {
  return (
    report.agentControllerRef === existingBinding.agentControllerRef &&
    normalizeOptionalText(
      report.runtimeReportSubmission.wakeLoopServiceRuntimeId
    ) === normalizeOptionalText(existingBinding.wakeLoopServiceRuntimeId)
  );
}

function emitSetupBootstrapLine(
  line: SetupBootstrapLineEmitter | undefined,
  input: Parameters<SetupBootstrapLineEmitter>[0]
): void {
  line?.(input);
}

function requireAcceptedAssistantBinding(input: {
  agentId: string;
  binding: AgentProfileRuntimeBindingEnsureResult;
  report: SelectedAgentControllerReportResult;
}): SetupBootstrapBinding {
  const wakeLoopServiceRuntimeId = normalizeOptionalText(
    input.report.runtimeReportSubmission.wakeLoopServiceRuntimeId
  );
  if (
    input.binding.status !== "accepted" ||
    input.binding.routable !== true ||
    !wakeLoopServiceRuntimeId
  ) {
    const reasonCode =
      input.binding.reasonCodes[0] ??
      input.binding.reason ??
      "agent_profile_binding.unavailable";
    throw new Error(
      `WakeLoop Assistant could not be connected to local agent ${input.agentId}. Support code: ${reasonCode}.`
    );
  }

  return {
    agentControllerRef: input.report.agentControllerRef,
    wakeLoopServiceRuntimeId,
    outcome: input.binding.outcome ?? "created",
    ...(input.binding.reason ? { reason: input.binding.reason } : {}),
    reasonCodes: [...input.binding.reasonCodes],
    routable: true,
    source: "agent_profile_binding_api",
    status: "accepted",
  };
}

async function createSetupWelcomingSpace(input: {
  activeOrganizationSlug: string;
  baseSpaceName: string;
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
  humanRequestContext: ReturnType<
    typeof buildWakeLoopRequestContextFromProfile
  >;
  spaceApi: ReturnType<typeof createCliSpaceApi>;
}): Promise<BootstrapWelcomingSpace> {
  const createdSpace = await input.spaceApi.createSpace({
    accessMode: "protected",
    name: input.baseSpaceName,
    password: buildDefaultWelcomingSpacePassword(),
    purpose: WELCOMING_SPACE_PURPOSE,
    requestContext: input.humanRequestContext,
    visibility: "private",
  });
  const createdSpaceName =
    normalizeOptionalText(createdSpace.meta?.name) ?? input.baseSpaceName;
  const createdAt = requireCreatedSpaceCreatedAt(createdSpace.meta);
  return {
    createdAt,
    creatorKind: resolveCreatedSpaceCreatorKind(createdSpace.meta) ?? "human",
    creatorProfileId:
      resolveCreatedSpaceCreatorProfileId(createdSpace.meta) ??
      input.humanProfile.wakeLoopProfileId,
    name: createdSpaceName,
    outcome: "created",
    spaceId: createdSpace.spaceId,
    updatedAt: normalizeOptionalText(createdSpace.meta?.updatedAt),
    url: buildSetupWelcomingSpaceUrl(
      input.gatewayUrl,
      input.activeOrganizationSlug,
      createdSpace.spaceId
    ),
  };
}

function toReusedWelcomingSpace(input: {
  activeOrganizationSlug: string;
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
  item: AccountSpaceIndexItem;
}): BootstrapWelcomingSpace {
  return {
    createdAt: requireAccountIndexSpaceCreatedAt(input.item),
    creatorKind: input.item.creatorKind ?? null,
    creatorProfileId: input.item.creatorProfileId ?? null,
    name: input.item.spaceName,
    outcome: "reused",
    spaceId: input.item.spaceId,
    updatedAt: input.item.spaceUpdatedAt,
    url: buildSetupWelcomingSpaceUrl(
      input.gatewayUrl,
      input.activeOrganizationSlug,
      input.item.spaceId
    ),
  };
}

function buildSetupWelcomingSpaceUrl(
  gatewayUrl: string,
  activeOrganizationSlug: string,
  spaceId: string
): string {
  // The org route resolves the browser account's current human profile. Do not
  // pin the setup CLI profile into a link that may be opened in another account.
  return buildSpaceWebUrl(gatewayUrl, activeOrganizationSlug, spaceId);
}

function requireAccountIndexSpaceCreatedAt(
  item: AccountSpaceIndexItem
): string {
  const createdAt = normalizeOptionalText(item.spaceCreatedAt);
  if (!createdAt) {
    throw new Error(
      `Managed WakeLoop Welcoming Space ${item.spaceId} is missing spaceCreatedAt in the account Space index.`
    );
  }
  return createdAt;
}

export function buildSetupWelcomeMessage(agentName: string): string {
  return buildSetupAssistantBootstrapWelcomeMessage({ agentName });
}

function buildDefaultWelcomingSpacePassword(): string {
  return randomBytes(32).toString("base64url");
}

async function syncBootstrapProfileSpaceHistory(input: {
  assistantProfile: WakeLoopProfile;
  gatewayUrl: string;
  humanProfile: WakeLoopProfile;
  welcomingSpace: BootstrapWelcomingSpace;
}): Promise<SetupBootstrapWarning[]> {
  const gatewayHost = resolveGatewayHost(input.gatewayUrl);
  const lastJoinedAt = new Date().toISOString();

  const warnings = await Promise.all(
    [input.humanProfile, input.assistantProfile].map(async (profile) => {
      try {
        await upsertProfileSpaceHistory({
          wakeLoopProfileId: profile.wakeLoopProfileId,
          baseUrl: input.gatewayUrl,
          requestContext: buildWakeLoopRequestContextFromProfile({
            wakeLoopProfile: profile,
          }),
          spaceId: input.welcomingSpace.spaceId,
          body: {
            creatorKind: input.welcomingSpace.creatorKind ?? "human",
            creatorProfileId:
              input.welcomingSpace.creatorProfileId ??
              input.humanProfile.wakeLoopProfileId,
            gatewayHost,
            lastJoinedAt,
            spaceCreatedAt: input.welcomingSpace.createdAt,
            ...(input.welcomingSpace.updatedAt
              ? { spaceUpdatedAt: input.welcomingSpace.updatedAt }
              : {}),
            spaceName: input.welcomingSpace.name,
            status: WELCOMING_SPACE_HISTORY_STATUS,
          },
        });
        return null;
      } catch (error) {
        return {
          code: "profile_space_history_sync_failed",
          message: toErrorMessage(error),
          profileId: profile.wakeLoopProfileId,
        } satisfies SetupBootstrapWarning;
      }
    })
  );

  return warnings.filter((warning) => warning !== null);
}

function resolveCreatedSpaceCreatorProfileId(meta?: SpaceMeta): string | null {
  const creator = meta?.creator;
  if (!(creator && typeof creator === "object")) {
    return null;
  }
  return normalizeOptionalText(
    "profileId" in creator
      ? (creator as { profileId?: unknown }).profileId
      : undefined
  );
}

function resolveCreatedSpaceCreatorKind(
  meta?: SpaceMeta
): "human" | "agent" | "system" | null {
  const kind = normalizeOptionalText(meta?.creator?.kind);
  return kind === "human" || kind === "agent" || kind === "system"
    ? kind
    : null;
}

function requireCreatedSpaceCreatedAt(meta?: SpaceMeta): string {
  const createdAt = normalizeOptionalText(meta?.createdAt);
  if (!createdAt) {
    throw new Error(
      "Space creation did not return meta.createdAt for setup bootstrap linkage."
    );
  }
  return createdAt;
}

function resolveBootstrapUserName(humanProfile: WakeLoopProfile): string {
  return requireNormalizedText(
    normalizeOptionalText(humanProfile.ownerName) ??
      normalizeOptionalText(humanProfile.profileName),
    "signed-in user name"
  );
}

function runBootstrapStep<T>(
  step: SetupBootstrapStep,
  progress: SetupProgressStepHandle | undefined,
  progressText: string,
  run: () => Promise<T>
): Promise<T> {
  progress?.detail({
    code: `setup.bootstrap.${step}`,
    text: progressText,
  });
  return run().catch((error: unknown) => {
    throw new SetupBootstrapStepError(step, error);
  });
}

class SetupBootstrapStepError extends Error {
  readonly causeValue: unknown;
  readonly step: SetupBootstrapStep;

  constructor(step: SetupBootstrapStep, causeValue: unknown) {
    super(`${step}: ${toErrorMessage(causeValue)}`);
    this.name = "SetupBootstrapStepError";
    this.step = step;
    this.causeValue = causeValue;
  }
}

function requireNormalizedText(value: unknown, label: string): string {
  const normalized = normalizeOptionalText(value);
  if (!normalized) {
    throw new Error(`${label} is required.`);
  }
  return normalized;
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  return "Unknown setup bootstrap error.";
}

function readErrorName(error: unknown): string | null {
  return error instanceof Error && error.name.trim().length > 0
    ? error.name
    : null;
}
